import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TacticEditor } from "./TacticEditor";
import { validateStatements } from "./tacticModel";
import "./TacticEditor.css";

// Backend call-rule 422 details (app/api/routes/tactics.py) -> i18n keys, so the
// user sees a localized message instead of the raw English. Keep in sync with
// the backend strings (both in this repo).
const CALL_ERR_I18N = {
  "This tactic is called by another, so it can't make calls": "tactic.errCalleeCantCall",
  "You can only call one of your own tactics": "tactic.errCallOwn",
  "You can only call a tactic that is NOT assigned to a player": "tactic.errCallAssigned",
  "A called tactic can't itself make calls": "tactic.errCalleeHasCalls",
};

/**
 * Full-screen modal wrapping TacticEditor.
 * Keeps a local copy of statements so Cancel discards changes.
 *
 * @param {{ name: string, statements: object[], onSave: (s: object[]) => void, onClose: () => void }} props
 */
export function TacticEditorModal({ tacticId, name, statements, onSave, onClose }) {
  const { t } = useTranslation();
  const [draft, setDraft]   = useState(statements);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const handleSave = async () => {
    const problems = validateStatements(draft);
    if (problems.length) {
      setError(t(`tacticEditor.${problems[0]}`));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
    } catch (err) {
      const key = CALL_ERR_I18N[err.message];
      setError(key ? t(key) : err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="tactic-modal-panel" onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <span className="modal-title">{name}</span>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="tactic-modal-body">
          <TacticEditor statements={draft} onUpdate={setDraft} tacticId={tacticId} />
        </div>

        {error && <div className="tactic-modal-error">{error}</div>}

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            <button className="modal-cancel-btn" onClick={onClose} disabled={saving}>{t("cancel")}</button>
            <button className="modal-save-btn" onClick={handleSave} disabled={saving}>{t("save")}</button>
          </div>
        </div>

      </div>
    </div>
  );
}
